import clsx from 'clsx'
import { Col, CreateTableState, Grid, Typography } from 'gu-ui-kit'
import i18n from 'i18next'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import styles from 'modules/Services/Access/List/network-account-access.module.scss'
import {
  DeleteButton,
  EditButton,
  StatusBadge,
} from 'modules/Services/Access/List/subcomponents'
import * as Sentry from '@sentry/browser'
import { notificationShow } from 'modules/Notifications/actions'
import { ACCESS_DELETE } from 'modules/Services/types'
import { useTranslation } from 'react-i18next'
import UserIcon from 'modules/Services/Access/List/subcomponents/UserIcon'

export const vkAds = ({ filters, processingIds, networkType, accountId }, data) => {
  const { t } = useTranslation()
  const breakpoints = useBreakpoint()
  const dispatch = useDispatch()
  const archiveView = useMemo(() => filters.status?.value === 'archived', [filters])
  const [columnVisibility, setColumnVisibility] = useState()

  useEffect(() => {
    switch (true) {
      case breakpoints.sm:
        setColumnVisibility({ is_new: false, view: false, access_type: false, client_name: false })
        break
      case breakpoints.mds:
        setColumnVisibility({ is_new: false, client_name: false })
        break
      case breakpoints.lgs:
        setColumnVisibility({ client_name: false })
        break
      default:
        setColumnVisibility({})
        break
    }
  }, [breakpoints])

  const tableConfig = {
    columns: [
      {
        id: 'agency_manager.username',
        header: t('services::accesses.table.head.name'),
        className: clsx(styles['accesses-list__cell'], styles['accesses-list__cell_name'], {
          [styles['accesses-list__cell_name-archived']]: archiveView,
        }),
        cell: ({
          // eslint-disable-next-line react/prop-types
          row: {
            // eslint-disable-next-line react/prop-types
            original,
          },
        }) => {
          const {
            // eslint-disable-next-line react/prop-types
            //id,
            agency_manager,
            access_type,
          } = original

          // eslint-disable-next-line react/prop-types
          !agency_manager?.username &&
            Sentry.captureMessage(
              'empty username, vk access data object: ' + JSON.stringify(original),
            )

          return (
            <div className={styles['accesses-list__col-layout']}>
              <Grid
                wrap={'nowrap'}
                justifyContent="start"
                alignItems="center"
              >
                <UserIcon />
                <Grid
                  fluid={false}
                  direction="column"
                  alignItems="start"
                  justifyContent="start"
                  gap={4}
                >
                  <Typography variant={'Body-20'} noMargin color={'inherit'}>
                    {/*<Link to={`access/${id}/edit`}>*/}
                    {/* eslint-disable-next-line react/prop-types */}
                    {agency_manager?.username ?? '—'}
                    {/*</Link>*/}
                  </Typography>

                  <Typography variant={'Body-15'} noMargin color={'Label-Secondary'}>
                    {/* eslint-disable-next-line react/prop-types */}
                    ID: {agency_manager?.external_id ?? '—'}
                  </Typography>
                  {breakpoints.lgs && agency_manager?.client_name && (
                    <Typography
                      component={'span'}
                      color={'Label-Secondary'}
                      variant={'Body-10'}
                    >
                      {t('services::accesses.table.head.client_name')}:{' '}
                      {agency_manager?.client_name}
                    </Typography>
                  )}

                  {breakpoints.sm && (
                    <StatusBadge
                      label={i18n.t(`services::accesses.roles.options.${access_type}`)}
                      status={access_type}
                      className={styles['accesses-list__status']}
                    />
                  )}
                </Grid>
              </Grid>
            </div>
          )
        },
      },
      {
        id: 'client_name',
        header: t('services::accesses.table.head.client_name'),
        className: clsx(styles['accesses-list__cell'], styles['accesses-list__cell_client-name']),
        cell: ({
          // eslint-disable-next-line react/prop-types
          row: {
            // eslint-disable-next-line react/prop-types
            original: {
              // eslint-disable-next-line react/prop-types
              agency_manager: { client_name = null } = {}, // eslint-disable-next-line react/prop-types
            },
          },
        }) => {
          return (
            <Typography
              color={'Label-Fifth'}
              variant={'Body-15'}
            >
              {client_name || '-'}
            </Typography>
          )
        },
      },
      {
        id: 'access_type',
        header: t('services::accesses.table.head.access_type'),
        className: clsx(styles['accesses-list__cell'], styles['accesses-list__cell_access-type']),
        cell: ({
          // eslint-disable-next-line react/prop-types
          row: {
            // eslint-disable-next-line react/prop-types
            original: {
              // eslint-disable-next-line react/prop-types
              access_type, // eslint-disable-next-line react/prop-types
            },
          },
        }) => {
          return (
            <div className={styles['accesses-list__col-layout']}>
              <Grid
                alignItems="start"
                justifyContent="start"
                direction="column"
                l={{
                  direction: 'row',
                  alignItems: 'center',
                  justifyContent: 'between',
                }}
              >
                <Col>
                  <StatusBadge
                    label={i18n.t(`services::accesses.roles.options.${access_type}`)}
                    status={access_type}
                    className={styles['accesses-list__status']}
                  />
                </Col>
              </Grid>
            </div>
          )
        },
      },
      {
        id: 'actions',
        className: styles['accesses-list__cell'],
        cell: ({
          // eslint-disable-next-line react/prop-types
          row: {
            // eslint-disable-next-line react/prop-types
            original: {
              // eslint-disable-next-line react/prop-types
              id, // eslint-disable-next-line react/prop-types
              name, // eslint-disable-next-line react/prop-types
            },
          },
        }) => (
          <Grid
            className={clsx(styles['accesses-list__cell_actions'])}
            justifyContent={'flex-end'}
            gap={16}
            wrap={false}
          >
            <DeleteButton
              className={styles['accesses-list__action-icon-button']}
              onClick={() =>
                new Promise((resolve, reject) =>
                  dispatch({
                    type: ACCESS_DELETE,
                    meta: { resolve, reject },
                    payload: {
                      accountId,
                      accessId: id,
                      action: 'delete',
                      networkType,
                    },
                  }),
                ).then(() => {
                  dispatch(
                    notificationShow({
                      type: 'success',
                      message: 'common::notifications.success_removed',
                    }),
                  )
                })
              }
              login={name}
              processing={processingIds.includes(id)}
            />
            <Link to={`access/${id}/edit`}>
              <EditButton />
            </Link>
          </Grid>
        ),
      },
    ],
    data,
    state: {
      columnVisibility: archiveView
        ? { is_new: false, ...columnVisibility }
        : { ...columnVisibility },
    },

    className: styles['accesses-list__table'],
    showFooter: false,
  }

  return {
    tableState: CreateTableState(tableConfig, data),
    tableConfig,
  }
}
